/**
 * @file: 执行单个新增任务
 * @author: xiaolong.huang@dmall.com
 */

import { Cube } from '../../cube/core/cube';
import * as CubeTree from '../../cubeTree/index';
import Utils from '../../utils/util';
import FrontCubeData from '../../frontCubeData/index';
import DragSyStem from '../../dragSyStem/index';
import Drag from '../drag/index';
import Select from '../select/index';
import Update from '../../update/index';
import CodiSystem from '../../codiSystem/index';
import { insertAfter } from './insertAfter/index';
import { getInsertInfo } from './getInsertInfo/index';
import { findInsertTarget } from './findInsertTarget/index';
import { insertToStagePosition } from './insertToStagePosition';

interface RecursiveRunTaskParams {
    insertTargets: Array<CubeId>,
    insertData: FreeObjMap,
    insertedIds: Array<CubeId>,
    done: (insertedIds: Array<CubeId>) => void,
    extendParams?: FreeObjMap
};

// addTask.extendParams
function runAddTask(addTask: AddTask): Promise<Array<CubeId>> {
    let insertData: FreeObjMap = CubeTree.makeACleanCube(addTask.cubeData, addTask.type);
    let insertTargets: Array<CubeId> = findInsertTarget(insertData, Select.getAll());

    return new Promise<Array<CubeId>>((resolve: (cubeIds: Array<CubeId>) => void, reject: () => void): void => {
        recursiveRunTask({
            insertTargets,
            insertData,
            extendParams: addTask.extendParams,
            insertedIds: [],
            done: (insertedIds: Array<CubeId>): void => {
                resolve(insertedIds);
            }
        });
    });
}

// 递归串行执行任务
async function recursiveRunTask(params: RecursiveRunTaskParams): Promise<void> {
    if (!params.insertTargets.length) {
        params.done(params.insertedIds);
        return;
    }

    let target: CubeId = params.insertTargets.shift();
    let insertTask: InsertInfo|null = getInsertInfo(params.insertData, target, params.extendParams);

    insertTask.extendParams = insertTask.extendParams || {};
    insertTask.extendParams = {
        ...insertTask.extendParams,
        ...params.extendParams
    };

    if (!insertTask) {
        recursiveRunTask(params);
        return;
    }

    let beAddedCube: CubeNode = await runInsertTask(insertTask);

    // 需要等待之前的任务执行完成后，再执行下一个任务
    params.insertedIds.push(beAddedCube.id);
    recursiveRunTask(params);
}

function runInsertTask(insertTask: InsertInfo): Promise<CubeNode> {
    return new Promise<CubeNode>((resolve: (cube: CubeNode) => void, reject: () => void): void => {
        let copyTask: InsertInfo = Utils.deepCopy(insertTask);
        let parentCube: CubeNode = CubeTree.findByCubeId(copyTask.parentId);
        let cubeInstance: CubeNode = new Cube(copyTask.insertData, parentCube, {
            created: (cube: CubeNode): void => {
                CubeTree.add(cube.id, cube);
                FrontCubeData.add(cube);
            }
        });

        // 挂载
        parentCube.add(cubeInstance, copyTask.index, (beAddedCube: CubeNode) => {
            insertAfter(beAddedCube, copyTask);

            // TODO: 删除insertafter中的坐标添加
            CodiSystem.deepScan(beAddedCube.id);
            DragSyStem.addDeep(beAddedCube.id);
            Drag.add(beAddedCube.id);
            resolve(beAddedCube);
        });
    });
}

export {
    runAddTask
};
